import { ErrorMessage } from "@/components/error-message";
import { LanguageSelect } from "@/components/language-select";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
} from "@/components/ui/form";
import { Input, ReadOnlyInput } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { dashboardPath } from "@/constants";
import i18n from "@/i18n";
import { useOnboardMutation } from "@/hooks/onboard";
import type { ErrorResponse } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { SelectValue } from "@radix-ui/react-select";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod/v4";

export const Route = createFileRoute("/onboard")({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (location.pathname === "/onboard") {
      if (context.auth.user.account) {
        const orgs = context.auth.user.account.orgs;
        if (!orgs) {
          return;
        }
        const path = orgs[0].slug + dashboardPath;
        throw redirect({ to: "../" + path });
      } else {
        throw redirect({
          to: "/auth/login",
        });
      }
    }
  },
});

const formSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, { error: () => i18n.t("onboard.validation.nameMin") })
    .max(100, { error: () => i18n.t("onboard.validation.nameMax") })
    .regex(/^[a-zA-Z0-9\s]+$/, {
      error: () => i18n.t("onboard.validation.nameChars"),
    }),
  currency: z.literal(["USD", "GBP", "EUR"], {
    error: () => i18n.t("onboard.validation.currency"),
  }),
});

type formSchemaType = z.infer<typeof formSchema>;

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

const currency = [
  {
    label: "$ (USD)",
    value: "USD",
  },
  {
    label: "£ (GBP)",
    value: "GBP",
  },
  {
    label: "€ (EUR)",
    value: "EUR",
  },
];

function RouteComponent() {
  const { t } = useTranslation();
  const form = useForm<formSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      currency: "USD",
    },
  });
  const mutation = useOnboardMutation();

  const watchedOrganization = form.watch("name");
  const previewSlug = watchedOrganization
    ? generateSlug(watchedOrganization)
    : "";

  const onSubmit: SubmitHandler<formSchemaType> = (data) => {
    mutation.mutate(data);
  };

  useEffect(() => {
    if (mutation.isError) {
      const err = mutation.error as unknown as ErrorResponse;

      console.log(err);
      Object.entries(err.msg).map(([key, value]) => {
        if (key === "name") {
          form.setError("name", { message: value });
        } else if (key === "currency") {
          form.setError(key, { message: value });
        }
      });
    }
  }, [mutation.isError]);

  return (
    <div className="flex h-screen items-center justify-center">
      <LanguageSelect showLabel className="fixed top-4 right-4 z-10" />
      <div className="w-full flex flex-col space-y-3">
        <div className="mx-auto w-full max-w-80 relative bottom-8 flex flex-col gap-6">
          <div className="flex w-full flex-col items-center gap-1">
            <h1 className="w-full text-center text-xl font-semibold tracking-tight text-primary">
              {t("onboard.title")}
            </h1>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="space-y-4 pb-3">
                <FormField
                  name="name"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="text"
                          maxLength={100}
                          placeholder={t("onboard.namePlaceholder")}
                          className="h-10 w-full bg-white text-sm font-normal placeholder:text-gray-400"
                          required
                          autoFocus
                          disabled={mutation.isPending}
                          {...field}
                        />
                      </FormControl>
                      <ErrorMessage
                        errors={form.formState.errors}
                        name="name"
                        render={({ message }) => (
                          <p className="text-sm text-destructive">{message}</p>
                        )}
                      />
                    </FormItem>
                  )}
                />

                <div className="space-y-2">
                  <div className="text-xs font-medium text-muted-foreground">
                    {t("onboard.slugPreview")}
                  </div>
                  <ReadOnlyInput>{previewSlug}</ReadOnlyInput>
                </div>
                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={
                          currency.find((cur) => cur.value === field.value)
                            ?.label
                        }
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue>
                              {
                                currency.find(
                                  (cur) => cur.value === field.value,
                                )?.label
                              }
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {currency.map((cur) => (
                            <SelectItem value={cur.value}>
                              {cur.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription className="text-xs font-medium text-muted-foreground">
                        {t("onboard.chooseCurrency")}
                      </FormDescription>

                      <ErrorMessage
                        errors={form.formState.errors}
                        name="currency"
                        render={({ message }) => (
                          <p className="text-sm text-destructive">{message}</p>
                        )}
                      />
                    </FormItem>
                  )}
                />
              </div>
              <Button
                type="submit"
                className="flex h-9 items-center justify-center text-white w-full rounded-sm"
                disabled={mutation.isPending}
              >
                {mutation.isPending ? t("common.updating") : t("common.continue")}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
